import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';

/**
 * Transient confirmations.
 *
 * Toasts report on things the operator did — a sync started, an alert acknowledged,
 * an export failed. They never carry information that exists nowhere else, they never
 * block, and errors stay put until dismissed because an error that vanishes in four
 * seconds may as well not have been shown.
 */

export type ToastTone = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** Milliseconds on screen. Errors default to staying until dismissed. */
  duration?: number;
  /** One optional follow-up: "View report", "Undo". */
  action?: { label: string; onClick: () => void };
}

interface ToastRecord extends ToastOptions {
  id: number;
  tone: ToastTone;
}

interface ToastContextValue {
  toast: (options: ToastOptions) => number;
  success: (title: string, description?: string) => number;
  error: (title: string, description?: string) => number;
  dismiss: (id: number) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONE_ICON = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
} as const;

const TONE_CLASS: Record<ToastTone, string> = {
  success: 'text-success',
  error: 'text-danger',
  warning: 'text-warning',
  info: 'text-info',
};

const MAX_VISIBLE = 4;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((item) => item.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    timers.current.forEach((timer) => window.clearTimeout(timer));
    timers.current.clear();
    setToasts([]);
  }, []);

  const toast = useCallback(
    (options: ToastOptions): number => {
      const id = nextId.current++;
      const tone = options.tone ?? 'info';
      const record: ToastRecord = { ...options, id, tone };

      setToasts((current) => {
        const next = [...current, record];
        // Keep the stack short: an operator does not read six of these.
        return next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
      });

      const duration = options.duration ?? (tone === 'error' ? 0 : 4500);
      if (duration > 0) {
        timers.current.set(
          id,
          window.setTimeout(() => dismiss(id), duration),
        );
      }
      return id;
    },
    [dismiss],
  );

  const success = useCallback(
    (title: string, description?: string) => toast({ title, description, tone: 'success' }),
    [toast],
  );
  const error = useCallback(
    (title: string, description?: string) => toast({ title, description, tone: 'error' }),
    [toast],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((timer) => window.clearTimeout(timer));
      map.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({ toast, success, error, dismiss, dismissAll }),
    [toast, success, error, dismiss, dismissAll],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined'
        ? createPortal(
            <div
              aria-live="polite"
              aria-atomic="false"
              // Above the float tier (drawers, modals, the palette all sit at
              // z-100): a toast that a drawer paints over is a message nobody
              // reads — which is exactly how two "it isn't working" reports
              // began, the refusal hidden behind the very drawer that caused it.
              className="pointer-events-none fixed inset-x-0 bottom-0 z-200 flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:items-end print-hide"
            >
              {toasts.map((item) => {
                const Icon = TONE_ICON[item.tone];
                return (
                  <div
                    key={item.id}
                    role={item.tone === 'error' ? 'alert' : 'status'}
                    className={clsx(
                      'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-md border border-line',
                      'bg-surface px-3.5 py-3 shadow-float animate-slide-up',
                    )}
                  >
                    <Icon
                      aria-hidden
                      className={clsx('mt-0.5 h-4 w-4 shrink-0', TONE_CLASS[item.tone])}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-micro font-medium text-ink">{item.title}</p>
                      {item.description ? (
                        <p className="mt-0.5 text-micro leading-relaxed text-muted">
                          {item.description}
                        </p>
                      ) : null}
                      {item.action ? (
                        <button
                          type="button"
                          onClick={() => {
                            item.action?.onClick();
                            dismiss(item.id);
                          }}
                          className="mt-1.5 text-micro font-medium text-accent hover:text-accent-strong"
                        >
                          {item.action.label}
                        </button>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => dismiss(item.id)}
                      aria-label="Dismiss"
                      className="-mt-0.5 -mr-1 shrink-0 rounded-sm p-1 text-faint hover:bg-surface-2 hover:text-ink"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside a ToastProvider');
  return ctx;
}
